import React, {useCallback, useMemo} from "react";
import styles from "./task-list-table.module.css";
import { ColumnVisibility, Task } from "../../types/public-types";

const localeDateStringCache:{[key: string]: string} = {};
const toLocaleDateStringFactory =
  (locale: string) =>
  (date: Date, dateTimeOptions: Intl.DateTimeFormatOptions) => {
    const key = date.toString();
    let lds = localeDateStringCache[key];
    if (!lds) {
      lds = date.toLocaleDateString(locale, dateTimeOptions);
      localeDateStringCache[key] = lds;
    }
    return lds;
  };
const dateTimeOptions: Intl.DateTimeFormatOptions = {
  weekday: "short",
  year: "numeric",
  month: "long",
  day: "numeric",
};

export interface TaskListTableProps<T extends Task> {
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  locale: string;
  tasks: T[];
  selectedTaskId: string;
  columnList: ColumnVisibility<T>[];
  setSelectedTask: (taskId: string) => void;
  onExpanderClick: (task: T) => void;
}

export type TaskListTablePropsType<T extends Task> = (prop : TaskListTableProps<T>) => React.ReactElement<any, any>;

export const TaskListTableDefault = <T extends Task, >({
  rowHeight,
  tasks,
  fontFamily,
  fontSize,
  locale,
  columnList,
  onExpanderClick,
}: TaskListTableProps<T>): React.ReactElement<any, any> => {
  const toLocaleDateString = useMemo(
    () => toLocaleDateStringFactory(locale),
    [locale]
  );
  const makeTitle = useCallback((task: T, columnVisibility: ColumnVisibility<T>): string => {
    return columnVisibility.toShow(task)?.toString() ?? "";
  },[tasks, columnList]);

  const makeContent = useCallback((task: T, columnVisibility: ColumnVisibility<T>): React.ReactNode => {
    if (columnVisibility.render !== undefined) {
      return columnVisibility.render(task);
    }
    if (columnVisibility.isDate === true) {
      return toLocaleDateString((columnVisibility.toShow(task) as Date), dateTimeOptions)
    }
    const content = columnVisibility.toShow(task);
    if (content instanceof Date) {
      return toLocaleDateString(content, dateTimeOptions);
    }
    return content ?? "";
  }, [tasks, columnList, toLocaleDateString, dateTimeOptions])

  return (
    <div
      className={styles.taskListWrapper}
      style={{
        fontFamily: fontFamily,
        fontSize: fontSize,
      }}
    >
      {tasks.map(t => {
        let expanderSymbol = "";
        if (t.hideChildren === false) {
          expanderSymbol = "▼";
        } else if (t.hideChildren === true) {
          expanderSymbol = "▶";
        }

        return (
          <div
            className={styles.taskListTableRow}
            style={{ height: rowHeight }}
            key={`${t.id}row`}
          >
            {columnList.map((columnVisibility,index) => {
              if (columnVisibility.isVisible === true) {
                if (columnVisibility.columnWithArrow === true) {
                  return (
                    <div
                      key={index}
                      className={styles.taskListCell}
                      style={{
                        minWidth: columnVisibility.width,
                        maxWidth: columnVisibility.width,
                      }}
                      // title={t.name}
                    >

                      <div className={styles.taskListNameWrapper}>
                        <div
                          className={
                            expanderSymbol
                              ? styles.taskListExpander
                              : styles.taskListEmptyExpander
                          }
                          onClick={() => onExpanderClick(t)}
                        >
                          {expanderSymbol}
                        </div>
                        <div
                        className={styles.cellText}
                        title={makeTitle(t, columnVisibility)}>{ makeContent(t, columnVisibility) }</div>
                      </div>

                    </div>
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className={styles.taskListCell}
                      style={{
                        minWidth: columnVisibility.width,
                        maxWidth: columnVisibility.width,
                      }}
                    >
                      &nbsp;{ makeContent(t, columnVisibility) }
                    </div>
                  );
                }
              }
              else {
                return (<div key={index}></div>);
              }
            })}
          </div>
        );
      })}
    </div>
  );
};
